/**
 * Pure operations over `DashboardLayout`. Every function returns a new
 * layout; nothing here touches React or the network.
 */

import {
  DASHBOARD_LAYOUT_VERSION,
  GRID_COLUMNS,
  MAX_WIDGET_H,
  MIN_WIDGET_H,
  MIN_WIDGET_W,
  type ChartSeriesConfig,
  type ChartType,
  type DashboardLayout,
  type DashboardSection,
  type DashboardWidget,
  type ForecastMethod,
  type WidgetKind,
} from './types';

export function newId(prefix = 'w'): string {
  const rand =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID().slice(0, 8)
      : Math.random().toString(36).slice(2, 10);
  return `${prefix}_${rand}`;
}

export function clampW(w: number): number {
  return Math.min(GRID_COLUMNS, Math.max(MIN_WIDGET_W, Math.round(w)));
}

export function clampH(h: number): number {
  return Math.min(MAX_WIDGET_H, Math.max(MIN_WIDGET_H, Math.round(h)));
}

/** Sensible starting footprint per widget kind (columns × rows). */
export const DEFAULT_SIZE: Record<WidgetKind, { w: number; h: number }> = {
  metric: { w: 3, h: 2 },
  chart: { w: 6, h: 4 },
  forecast: { w: 6, h: 4 },
  table: { w: 6, h: 4 },
  text: { w: 4, h: 1 },
};

/** An unconfigured widget of the given kind, ready for the editor. */
export function blankWidget(kind: WidgetKind): DashboardWidget {
  const base = { id: newId(), ...DEFAULT_SIZE[kind] };
  switch (kind) {
    case 'metric':
      return { kind, ...base, metric: '', showSparkline: true, showDelta: true, target: null };
    case 'chart':
      return { kind, ...base, chartType: 'line', series: [], grain: 'auto', compare: false, cumulative: false, showLegend: true };
    case 'forecast':
      return { kind, ...base, source: '', method: 'auto', horizon: 0, history: 0, showBand: true, showManual: true, itemId: null };
    case 'table':
      return { kind, ...base, table: '', columns: [], limit: 0, sort: null };
    case 'text':
      return { kind, ...base, body: '' };
  }
}

export const WIDTH_PRESETS: Array<{ w: number; label: string }> = [
  { w: 3, label: '¼ width' },
  { w: 4, label: '⅓ width' },
  { w: 6, label: '½ width' },
  { w: 8, label: '⅔ width' },
  { w: 12, label: 'Full width' },
];

export const HEIGHT_PRESETS: Array<{ h: number; label: string }> = [
  { h: 1, label: 'Compact' },
  { h: 2, label: 'Short' },
  { h: 3, label: 'Medium' },
  { h: 4, label: 'Tall' },
  { h: 6, label: 'Extra tall' },
];

// ---------------------------------------------------------------------------
// Parsing — stored JSON is untrusted
// ---------------------------------------------------------------------------

const CHART_TYPES: ReadonlySet<string> = new Set<ChartType>(['line', 'area', 'bar', 'stacked', 'combo', 'donut', 'hbar']);
const FORECAST_METHODS: ReadonlySet<string> = new Set<ForecastMethod>(['auto', 'linear', 'holt', 'seasonal', 'moving_avg']);

function str(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function bool(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function num(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function parseSeries(value: unknown): ChartSeriesConfig[] {
  if (!Array.isArray(value)) return [];
  const out: ChartSeriesConfig[] = [];
  for (const entry of value) {
    if (!entry || typeof entry !== 'object') continue;
    const e = entry as Record<string, unknown>;
    const metric = str(e.metric);
    if (!metric) continue;
    const cfg: ChartSeriesConfig = { metric };
    if (typeof e.color === 'string' && /^#[0-9a-f]{6}$/i.test(e.color)) cfg.color = e.color;
    if (e.kind === 'bar' || e.kind === 'line' || e.kind === 'area') cfg.kind = e.kind;
    if (e.axis === 'left' || e.axis === 'right') cfg.axis = e.axis;
    out.push(cfg);
  }
  return out;
}

export function parseWidget(value: unknown): DashboardWidget | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  const kind = raw.kind;
  const id = str(raw.id) || newId();
  const title = typeof raw.title === 'string' && raw.title.trim() ? raw.title.trim() : undefined;
  const size = (k: WidgetKind) => ({
    w: clampW(num(raw.w, DEFAULT_SIZE[k].w)),
    h: clampH(num(raw.h, DEFAULT_SIZE[k].h)),
  });
  switch (kind) {
    case 'metric': {
      const metric = str(raw.metric);
      if (!metric) return null;
      const target = typeof raw.target === 'number' && Number.isFinite(raw.target) ? raw.target : null;
      return {
        kind,
        id,
        title,
        ...size('metric'),
        metric,
        showSparkline: bool(raw.showSparkline, true),
        showDelta: bool(raw.showDelta, true),
        target,
      };
    }
    case 'chart': {
      const chartType = CHART_TYPES.has(str(raw.chartType)) ? (raw.chartType as ChartType) : 'line';
      const grain = raw.grain === 'day' || raw.grain === 'week' || raw.grain === 'month' ? raw.grain : 'auto';
      const breakdown = str(raw.breakdown) || undefined;
      const limit = typeof raw.limit === 'number' ? Math.min(20, Math.max(3, Math.round(raw.limit))) : undefined;
      return {
        kind,
        id,
        title,
        ...size('chart'),
        chartType,
        series: parseSeries(raw.series),
        breakdown,
        grain,
        compare: bool(raw.compare, false),
        cumulative: bool(raw.cumulative, false),
        showLegend: bool(raw.showLegend, true),
        limit,
      };
    }
    case 'forecast': {
      const source = str(raw.source);
      if (!source) return null;
      return {
        kind,
        id,
        title,
        ...size('forecast'),
        source,
        method: FORECAST_METHODS.has(str(raw.method)) ? (raw.method as ForecastMethod) : 'auto',
        horizon: Math.max(0, Math.min(24, Math.round(num(raw.horizon, 0)))),
        history: Math.max(0, Math.min(36, Math.round(num(raw.history, 0)))),
        showBand: bool(raw.showBand, true),
        showManual: bool(raw.showManual, true),
        itemId: typeof raw.itemId === 'string' && raw.itemId ? raw.itemId : null,
      };
    }
    case 'table': {
      const table = str(raw.table);
      if (!table) return null;
      const columns = Array.isArray(raw.columns) ? raw.columns.filter((c): c is string => typeof c === 'string') : [];
      let sort: { key: string; dir: 'asc' | 'desc' } | null = null;
      if (raw.sort && typeof raw.sort === 'object') {
        const s = raw.sort as Record<string, unknown>;
        if (typeof s.key === 'string' && (s.dir === 'asc' || s.dir === 'desc')) sort = { key: s.key, dir: s.dir };
      }
      return {
        kind,
        id,
        title,
        ...size('table'),
        table,
        columns,
        limit: Math.max(0, Math.min(100, Math.round(num(raw.limit, 0)))),
        sort,
      };
    }
    case 'text':
      return { kind, id, title, ...size('text'), body: str(raw.body) };
    default:
      return null;
  }
}

export function parseSection(value: unknown): DashboardSection | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  const widgets = Array.isArray(raw.widgets)
    ? raw.widgets.map(parseWidget).filter((w): w is DashboardWidget => w !== null)
    : [];
  return {
    id: str(raw.id) || newId('s'),
    title: str(raw.title, 'Section'),
    description: typeof raw.description === 'string' && raw.description.trim() ? raw.description : undefined,
    collapsed: bool(raw.collapsed, false) || undefined,
    widgets,
  };
}

/**
 * Narrows stored JSON to a layout. Returns null when nothing usable is
 * stored so callers can fall back to the default. Unknown source ids are
 * kept on purpose — they render as "unavailable" instead of vanishing.
 */
export function parseLayout(value: unknown): DashboardLayout | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const raw = value as Record<string, unknown>;
  if (!Array.isArray(raw.sections)) return null;
  const sections = raw.sections.map(parseSection).filter((s): s is DashboardSection => s !== null);
  const seen = new Set<string>();
  for (const section of sections) {
    if (seen.has(section.id)) section.id = newId('s');
    seen.add(section.id);
    for (const widget of section.widgets) {
      if (seen.has(widget.id)) widget.id = newId();
      seen.add(widget.id);
    }
  }
  return { version: DASHBOARD_LAYOUT_VERSION, sections };
}

export function emptyLayout(): DashboardLayout {
  return { version: DASHBOARD_LAYOUT_VERSION, sections: [] };
}

// ---------------------------------------------------------------------------
// Lookups
// ---------------------------------------------------------------------------

export interface WidgetLocation {
  section: DashboardSection;
  sectionIndex: number;
  widget: DashboardWidget;
  index: number;
}

export function findWidget(layout: DashboardLayout, widgetId: string): WidgetLocation | null {
  for (let si = 0; si < layout.sections.length; si++) {
    const section = layout.sections[si];
    const index = section.widgets.findIndex((w) => w.id === widgetId);
    if (index >= 0) return { section, sectionIndex: si, widget: section.widgets[index], index };
  }
  return null;
}

export function allWidgets(layout: DashboardLayout): DashboardWidget[] {
  return layout.sections.flatMap((s) => s.widgets);
}

// ---------------------------------------------------------------------------
// Widget edits
// ---------------------------------------------------------------------------

function mapSection(layout: DashboardLayout, sectionId: string, fn: (s: DashboardSection) => DashboardSection): DashboardLayout {
  return { ...layout, sections: layout.sections.map((s) => (s.id === sectionId ? fn(s) : s)) };
}

export function addWidget(layout: DashboardLayout, sectionId: string, widget: DashboardWidget, index?: number): DashboardLayout {
  return mapSection(layout, sectionId, (s) => {
    const widgets = [...s.widgets];
    widgets.splice(index == null ? widgets.length : Math.max(0, Math.min(widgets.length, index)), 0, widget);
    return { ...s, widgets };
  });
}

export function updateWidget(layout: DashboardLayout, widgetId: string, patch: Partial<DashboardWidget>): DashboardLayout {
  return {
    ...layout,
    sections: layout.sections.map((s) => ({
      ...s,
      widgets: s.widgets.map((w) => (w.id === widgetId ? ({ ...w, ...patch } as DashboardWidget) : w)),
    })),
  };
}

export function replaceWidget(layout: DashboardLayout, widget: DashboardWidget): DashboardLayout {
  return {
    ...layout,
    sections: layout.sections.map((s) => ({
      ...s,
      widgets: s.widgets.map((w) => (w.id === widget.id ? widget : w)),
    })),
  };
}

export function removeWidget(layout: DashboardLayout, widgetId: string): DashboardLayout {
  return {
    ...layout,
    sections: layout.sections.map((s) => ({ ...s, widgets: s.widgets.filter((w) => w.id !== widgetId) })),
  };
}

export function duplicateWidget(layout: DashboardLayout, widgetId: string): DashboardLayout {
  const loc = findWidget(layout, widgetId);
  if (!loc) return layout;
  const copy = { ...loc.widget, id: newId() } as DashboardWidget;
  return addWidget(layout, loc.section.id, copy, loc.index + 1);
}

export function resizeWidget(layout: DashboardLayout, widgetId: string, w: number, h: number): DashboardLayout {
  return updateWidget(layout, widgetId, { w: clampW(w), h: clampH(h) });
}

/**
 * Moves a widget to `toIndex` within `toSectionId`. `toIndex` is the index
 * in the destination list *before* removal, matching what a drop target
 * computes from the DOM.
 */
export function moveWidget(layout: DashboardLayout, widgetId: string, toSectionId: string, toIndex: number): DashboardLayout {
  const loc = findWidget(layout, widgetId);
  if (!loc) return layout;
  let target = toIndex;
  if (loc.section.id === toSectionId && loc.index < toIndex) target -= 1;
  const without = removeWidget(layout, widgetId);
  return addWidget(without, toSectionId, loc.widget, target);
}

/** Keyboard / menu fallback for drag: shift one position within the section. */
export function nudgeWidget(layout: DashboardLayout, widgetId: string, direction: -1 | 1): DashboardLayout {
  const loc = findWidget(layout, widgetId);
  if (!loc) return layout;
  const next = loc.index + direction;
  if (next < 0 || next >= loc.section.widgets.length) return layout;
  const widgets = [...loc.section.widgets];
  [widgets[loc.index], widgets[next]] = [widgets[next], widgets[loc.index]];
  return mapSection(layout, loc.section.id, (s) => ({ ...s, widgets }));
}

// ---------------------------------------------------------------------------
// Section edits
// ---------------------------------------------------------------------------

export function addSection(layout: DashboardLayout, title: string, index?: number): { layout: DashboardLayout; id: string } {
  const id = newId('s');
  const sections = [...layout.sections];
  sections.splice(index == null ? sections.length : index, 0, { id, title, widgets: [] });
  return { layout: { ...layout, sections }, id };
}

export function updateSection(
  layout: DashboardLayout,
  sectionId: string,
  patch: Partial<Omit<DashboardSection, 'id' | 'widgets'>>,
): DashboardLayout {
  return mapSection(layout, sectionId, (s) => ({ ...s, ...patch }));
}

export function removeSection(layout: DashboardLayout, sectionId: string): DashboardLayout {
  return { ...layout, sections: layout.sections.filter((s) => s.id !== sectionId) };
}

export function moveSection(layout: DashboardLayout, sectionId: string, direction: -1 | 1): DashboardLayout {
  const index = layout.sections.findIndex((s) => s.id === sectionId);
  const next = index + direction;
  if (index < 0 || next < 0 || next >= layout.sections.length) return layout;
  const sections = [...layout.sections];
  [sections[index], sections[next]] = [sections[next], sections[index]];
  return { ...layout, sections };
}

export function layoutsEqual(a: DashboardLayout | null, b: DashboardLayout | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return JSON.stringify(a) === JSON.stringify(b);
}
